import { Inject, LOCALE_ID, Pipe, PipeTransform } from '@angular/core';
import { NaDecimalPipe } from './na-decimal.pipe';
import { combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { UserSettingsQuery } from '../../features/user-settings/state/user-settings.query';
import { filterNilValue } from '@datorama/akita';

@Pipe({
    name: 'formattedNumber$',
    standalone: true
})
export class FormattedNumberPipe extends NaDecimalPipe implements PipeTransform {
    constructor(
        @Inject(LOCALE_ID) locale: string,
        private userSettingsQuery: UserSettingsQuery
    ) {
        super(locale);
    }

    /**
     * Transforms a given number into a formatted string according to user settings.
     *
     * @param {number} number - The number to be formatted.
     * @param {string} [nullValue] - The value to return if the input is null.
     * @param {string} [overrideDigitsInfo] - Optional. Overrides the digit formatting.
     *
     * @returns {Observable<string>} An Observable emitting the formatted string.
     *
     * @example
     * this.transform(1234);  // returns an Observable emitting "1,234" or as per user settings
     * this.transform(1234, null, '1.0-0');  // returns an Observable emitting "1234" ignoring decimals
     */
    transform(
        number: number | null,
        nullValue?: string,
        overrideDigitsInfo?: string,
        transformSign: 'only-positive' | 'only-negative' = null
    ): Observable<string> {
        if (transformSign) {
            // e.g., if we want to allow only positive or negative values
            number = Math.abs(number);
            number = transformSign === 'only-positive' ? number : -1 * number;
        }
        const numberOfDigits = (Math.log(Math.abs(number)) * Math.LOG10E + 1) | 0;
        let obs$: Observable<[number, number]>;
        if (numberOfDigits <= 3) {
            obs$ = this.userSettingsQuery.selectGlobalSetting$('DIGITS_SMALL');
        } else {
            obs$ = this.userSettingsQuery.selectGlobalSetting$('DIGITS_LARGE');
        }

        return combineLatest([
            this.userSettingsQuery.selectGlobalSetting$('LOCALE'),
            obs$.pipe(
                filterNilValue(),
                map((digits) => '1.' + digits.join('-'))
            )
        ]).pipe(
            map(([locale, digitsInfo]) => {
                return super.transform(
                    number,
                    overrideDigitsInfo || digitsInfo,
                    locale,
                    nullValue
                );
            })
        );
    }
}
